import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';

const NosotrosPage = () => {
    const { theme, toggleTheme } = useTheme();

    const ciudades = [
        'Lima', 'Arequipa', 'Juliaca', 'Tacna', 'Cusco', 'Trujillo', 'Huancayo'
    ];

    const canales = [
        { icon: 'pi pi-whatsapp', label: 'WhatsApp', value: '+51 978 326 796' },
        { icon: 'pi pi-tiktok', label: 'TikTok', value: '@laptops_pe' },
        { icon: 'pi pi-facebook', label: 'Facebook', value: 'Laptops.pe' },
        { icon: 'pi pi-instagram', label: 'Instagram', value: '@laptops.pe' },
    ];

    const garantias = [
        'Sitio web con certificado SSL (https) y protección contra fraudes.',
        'Métodos de pago seguros y verificados (tarjetas, Yape, Plin, PagoEfectivo, transferencias).',
        'Boletas y facturas electrónicas válidas ante SUNAT.',
        'Envíos garantizados con seguimiento en tiempo real.',
        'Equipos nuevos, originales y sellados, con garantía del fabricante.'
    ];

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Nosotros - Laptops.pe</title>
                <meta name="description" content="Conoce quiénes somos en Laptops.pe, cobertura nacional, atención 100% en línea y compra segura respaldada por Digital Wins." />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || ''}/nosotros`} />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />

            <main className="landing-main">
                <div className=" py-6">
                    {/* Título principal */}
                    <div className="text-center mb-5">
                        <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-3">
                            <span role="img" aria-label="nosotros">🧭</span> Nosotros – Laptops.pe
                        </h1>
                        <p className="text-700 text-lg">Conoce nuestra misión, cobertura y el respaldo que te ofrecemos.</p>
                    </div>

                    {/* ¿Quiénes somos? */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-briefcase text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">¿Quiénes somos?</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                Laptops.pe es una tienda online peruana especializada en la venta de laptops, notebooks, accesorios y tecnología de alto rendimiento.
                            </p>
                            <p className="text-700 line-height-3">
                                Somos parte de <strong>DIGITAL WINS E.I.R.L.</strong> (RUC: <strong>20612859630</strong>), empresa peruana dedicada al comercio electrónico y la distribución tecnológica en todo el país.
                            </p>
                            <p className="text-700 line-height-3">
                                Nuestro objetivo es ofrecer equipos de calidad al mejor precio, con la seguridad y respaldo que nuestros clientes merecen. Creemos firmemente que comprar por Internet debe ser fácil, rápido y seguro, por eso hemos desarrollado una plataforma moderna y transparente, diseñada para brindar una experiencia de compra confiable.
                            </p>
                            <Divider />
                            <div className="flex flex-wrap gap-2">
                                <Badge value="Calidad garantizada" severity="success" />
                                <Badge value="Precios competitivos" severity="info" />
                                <Badge value="Plataforma moderna" severity="warning" />
                            </div>
                        </div>
                    </Card>

                    {/* Cobertura nacional */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-truck text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">Cobertura nacional</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                Contamos con canales de distribución en Lima, Arequipa, Juliaca, Tacna, Cusco, Trujillo y Huancayo, desde donde gestionamos los envíos a todo el Perú.
                            </p>
                            <p className="text-700 line-height-3">
                                Estos puntos funcionan como <strong>almacenes logísticos</strong> y no como tiendas físicas, lo que nos permite reducir costos operativos y trasladar esos ahorros directamente a nuestros clientes en forma de descuentos.
                                Nuestra <strong>dirección fiscal</strong> es: PJ. EL TRIUNFO MZA. S LOTE. 37A INT. 502 URB. SAN LUIS, distrito San Luis, provincia Lima, departamento Lima. Nuestras <strong>oficinas administrativas</strong> están ubicadas en Urb. Brisas de Huachipa Mz. B Lte. 14, distrito Lurigancho, provincia Lima, departamento Lima.
                            </p>
                            <p className="text-700 line-height-3">
                                Nuestro compromiso es que tu pedido llegue de forma rápida, segura y en perfectas condiciones, sin importar en qué parte del país te encuentres.
                            </p>
                            <div className="grid mt-3">
                                {ciudades.map((ciudad) => (
                                    <div key={ciudad} className="col-6 md:col-3 lg:col-2 text-center mb-2">
                                        <Badge value={ciudad} severity="info" className="w-full" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>

                    {/* Atención 100% en línea */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-users text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">Atención 100% en línea</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                En Laptops.pe contamos con un equipo humano especializado en atención al cliente, disponible en línea para guiarte antes, durante y después de tu compra.
                            </p>
                            <p className="text-700 line-height-3">
                                Brindamos soporte a través de nuestros canales oficiales:
                            </p>
                            <div className="grid mt-2">
                                {canales.map((c) => (
                                    <div key={c.label} className="col-12 md:col-6 lg:col-3">
                                        <Panel header={c.label} className="h-full">
                                            <div className="flex align-items-center gap-2">
                                                <i className={`${c.icon} text-2xl text-primary`} aria-hidden="true"></i>
                                                <span className="text-700">{c.value}</span>
                                            </div>
                                        </Panel>
                                    </div>
                                ))}
                            </div>
                            <p className="text-700 line-height-3 mt-3">
                                Te acompañamos en todo el proceso, desde la elección del producto hasta la entrega final, asegurando una experiencia de compra transparente y confiable.
                            </p>
                        </div>
                    </Card>

                    {/* Compra con confianza */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-shield text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">Compra con confianza</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                Sabemos que en el Perú muchas personas aún tienen dudas sobre comprar por Internet, por eso hemos implementado sistemas de seguridad avanzados para proteger tus datos y garantizar una experiencia segura:
                            </p>
                            <ul className="list-none p-0 m-0">
                                {garantias.map((g, idx) => (
                                    <li key={idx} className="flex align-items-start gap-2 mb-2">
                                        <i className="pi pi-check-circle text-green-500" aria-hidden="true"></i>
                                        <span className="text-700">{g}</span>
                                    </li>
                                ))}
                            </ul>
                            <Divider />
                            <div className="surface-100 border-round p-3">
                                <p className="text-700 line-height-3 m-0">
                                    Tu confianza es nuestra prioridad, por eso cada compra realizada en Laptops.pe está respaldada por <strong>DIGITAL WINS E.I.R.L.</strong>, empresa formalmente constituida en el Perú.
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* Nuestra promesa */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-comment text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">Nuestra promesa</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                En Laptops.pe trabajamos para que cada cliente se sienta seguro de comprar en línea. No somos solo una tienda, somos tu aliado tecnológico.
                            </p>
                            <p className="text-700 line-height-3">
                                Queremos que cada laptop que llegue a tu hogar represente una inversión segura, inteligente y con respaldo real.
                            </p>
                        </div>
                    </Card>

                    {/* Nuestra visión */}
                    <Card className="mb-5">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-globe text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">Nuestra visión</h2>
                            </div>
                            <p className="text-700 line-height-3">
                                Convertirnos en la plataforma de venta de laptops más confiable y accesible del Perú, acercando la tecnología a todos los rincones del país con precios justos, atención cercana y total transparencia.
                            </p>
                        </div>
                    </Card>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default NosotrosPage;
